'use client';

import { FC, ReactNode, forwardRef, ButtonHTMLAttributes, AnchorHTMLAttributes } from 'react';
import Link from 'next/link';
import styles from './HotdeskButton.module.scss';
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'link';
type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonShape = 'pill' | 'rounded' | 'circle';
interface BaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  fullWidth?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  children?: ReactNode;
}
type ButtonAsButton = BaseProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
  as?: 'button';
  href?: never;
};
type ButtonAsLink = BaseProps & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
  as: 'link';
  href: string;
  target?: string;
  rel?: string;
};
type ButtonAsNextLink = BaseProps & {
  as: 'next-link';
  href: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
};
export type HotdeskButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsNextLink;
const HotdeskButton: FC<HotdeskButtonProps> = props => {
  const {
    variant = 'primary',
    size = 'md',
    shape = 'pill',
    fullWidth = false,
    iconLeft,
    iconRight,
    className,
    children,
    ...rest
  } = props;
  const classNames = [styles.btn, styles[`variant_${variant}`], styles[`size_${size}`], styles[`shape_${shape}`], fullWidth ? styles.fullWidth : '', !children && (iconLeft || iconRight) ? styles.iconOnly : '', className || ''].filter(Boolean).join(' ');
  const content = <>
            {iconLeft && <span className={styles.iconLeft}>{iconLeft}</span>}
            {children && <span className={styles.label}>{children}</span>}
            {iconRight && <span className={styles.iconRight}>{iconRight}</span>}
        </>;
  if (props.as === 'next-link') {
    const {
      as: _,
      href,
      target,
      rel,
      onClick,
      ...linkRest
    } = rest as any;
    return <Link href={href} className={classNames} target={target} rel={rel} onClick={onClick}>
                {content}
            </Link>;
  }
  if (props.as === 'link') {
    const {
      as: _,
      href,
      target,
      rel,
      ...anchorRest
    } = rest as any;
    return <a href={href} className={classNames} target={target} rel={rel} {...anchorRest}>
                {content}
            </a>;
  }
  const {
    as: _,
    ...buttonRest
  } = rest as any;
  return <button type="button" className={classNames} {...buttonRest} data-sentry-component="HotdeskButton" data-sentry-source-file="HotdeskButton.tsx">
            {content}
        </button>;
};
export default HotdeskButton;